import React, { useEffect, useState } from 'react';
import { CardNumberElement, CardExpiryElement, CardCvcElement, Elements, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { CreditCard, Star, Trash2, AlertCircle, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from '../api/client';
import { API } from '../api/config';
import { stripeElementStyle } from '../stripeElementStyle';

// Settings > Payment Card Management — real Stripe SetupIntent card storage
// for future payments. Exactly one saved card is the default once any
// exist (auto-assigned on first save, re-assignable, promoted
// automatically if the default is removed).

const stripePk = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_TYooMQauvdEDq54NiTphI7jx';
const stripePromise = loadStripe(stripePk);

interface PaymentCard {
  id: string;
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
  is_default: boolean;
}

const brandLabels: Record<string, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'American Express',
  discover: 'Discover',
};

const AddCardForm: React.FC<{ userId: string; onAdded: () => void; onCancel: () => void }> = ({ userId, onAdded, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    setCardError(null);

    try {
      const intentRes = await axios.post(API.createPaymentCardSetupIntent(userId), {}, { headers: authHeaders() });
      const { client_secret } = intentRes.data;

      const cardNumber = elements.getElement(CardNumberElement);
      if (!cardNumber) { setCardError('Card element not found. Please refresh and try again.'); setProcessing(false); return; }

      const { error: confirmError, setupIntent } = await stripe.confirmCardSetup(client_secret, {
        payment_method: { card: cardNumber },
      });

      if (confirmError) {
        setCardError(confirmError.message || 'Could not save the card. Please check your details.');
        setProcessing(false);
        return;
      }
      if (setupIntent?.status !== 'succeeded') {
        setCardError('Unexpected status: ' + setupIntent?.status + '. Please contact support.');
        setProcessing(false);
        return;
      }

      onAdded();
    } catch (err: any) {
      setCardError(err.response?.data?.detail || err.message || 'Failed to save the card. Please try again.');
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSave} style={{ display: 'grid', gap: '12px', padding: '16px', border: '1px solid var(--border-light)', borderRadius: '6px', background: 'var(--bg-oatmeal)' }}>
      <div className="form-group" style={{ margin: 0 }}>
        <label className="form-label">Card Number</label>
        <div style={{ padding: '12px', background: '#fff', border: '1px solid var(--sidebar-border)', borderRadius: '6px', minHeight: '44px', display: 'flex', alignItems: 'center' }}>
          <CardNumberElement options={{ style: stripeElementStyle }} onChange={(e) => { setCardComplete(e.complete); setCardError(e.error ? e.error.message : null); }} />
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <div>
          <label className="form-label">Expiry Date</label>
          <div style={{ padding: '12px', background: '#fff', border: '1px solid var(--sidebar-border)', borderRadius: '6px', minHeight: '44px', display: 'flex', alignItems: 'center' }}>
            <CardExpiryElement options={{ style: stripeElementStyle }} />
          </div>
        </div>
        <div>
          <label className="form-label">CVC</label>
          <div style={{ padding: '12px', background: '#fff', border: '1px solid var(--sidebar-border)', borderRadius: '6px', minHeight: '44px', display: 'flex', alignItems: 'center' }}>
            <CardCvcElement options={{ style: stripeElementStyle }} />
          </div>
        </div>
      </div>
      {cardError && (
        <div style={{ color: '#991B1B', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px', background: '#FEF2F2', padding: '10px', borderRadius: '6px' }}>
          <AlertCircle size={14} /> {cardError}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
        <button type="button" className="btn btn-outline" style={{ padding: '6px 12px', fontSize: '12px' }} onClick={onCancel} disabled={processing}>Cancel</button>
        <button type="submit" className="btn btn-primary" style={{ padding: '6px 12px', fontSize: '12px' }} disabled={!stripe || !cardComplete || processing}>
          {processing ? <><Loader2 className="animate-spin" size={14} /> Saving...</> : 'Save Card'}
        </button>
      </div>
    </form>
  );
};

const PaymentCardSettings: React.FC = () => {
  const { user } = useAuth();
  const [cards, setCards] = useState<PaymentCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [settingDefaultId, setSettingDefaultId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState('');

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

  const fetchCards = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const res = await axios.get(API.listPaymentCards(user.id), { headers: authHeaders() });
      setCards(res.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load saved cards.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const makeDefault = async (paymentMethodId: string) => {
    if (!user) return;
    setSettingDefaultId(paymentMethodId);
    setError('');
    try {
      await axios.put(API.setDefaultPaymentCard(user.id, paymentMethodId), {}, { headers: authHeaders() });
      await fetchCards();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to set the default card.');
    } finally {
      setSettingDefaultId(null);
    }
  };

  const deleteCard = async (paymentMethodId: string) => {
    if (!user) return;
    if (!window.confirm('Remove this saved card?')) return;
    setDeletingId(paymentMethodId);
    setError('');
    try {
      await axios.delete(API.deletePaymentCard(user.id, paymentMethodId), { headers: authHeaders() });
      await fetchCards();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to remove the card.');
    } finally {
      setDeletingId(null);
    }
  };

  if (!user) return <div>Loading...</div>;

  return (
    <div>
      <h1 className="page-title" style={{ marginBottom: '32px' }}>Payment Card Management</h1>

      <div className="section-card">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
          <CreditCard size={20} className="text-primary" />
          <h3 className="card-title" style={{ margin: 0 }}>Saved Cards</h3>
        </div>
        <p style={{ fontSize: '13px', color: '#64748B', marginTop: '-16px', marginBottom: '20px' }}>
          Save a card here for future payments. One card is always your default.
        </p>

        {error && (
          <div style={{ marginBottom: '16px', padding: '12px 16px', borderRadius: '8px', background: '#FEF2F2', color: '#991B1B', fontSize: '13px' }}>
            {error}
          </div>
        )}

        {loading ? (
          <p style={{ fontSize: '13px', color: '#64748B' }}>Loading...</p>
        ) : (
          <div style={{ display: 'grid', gap: '10px', marginBottom: '16px' }}>
            {cards.length === 0 && !adding && (
              <p style={{ fontSize: '13px', color: '#94A3B8' }}>No saved cards yet.</p>
            )}
            {cards.map((card) => (
              <div key={card.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', padding: '12px', border: `1px solid ${card.is_default ? 'var(--accent-moss)' : 'var(--border-light)'}`, borderRadius: '6px' }}>
                <div style={{ fontSize: '13px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontWeight: 600 }}>{brandLabels[card.brand] || card.brand}</span>
                  <span>•••• {card.last4}</span>
                  <span style={{ color: '#64748B' }}>Exp {String(card.exp_month).padStart(2, '0')}/{card.exp_year}</span>
                  {card.is_default && (
                    <span className="status-pill" style={{ background: '#F0FDF4', color: '#166534', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Star size={10} fill="currentColor" /> Default
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                  {!card.is_default && (
                    <button className="btn btn-outline" style={{ padding: '6px 10px', fontSize: '12px' }} disabled={settingDefaultId === card.id} onClick={() => makeDefault(card.id)}>
                      {settingDefaultId === card.id ? 'Setting...' : 'Set Default'}
                    </button>
                  )}
                  <button className="btn btn-outline" style={{ padding: '6px 10px', color: '#DC2626', borderColor: '#FCA5A5' }} disabled={deletingId === card.id} onClick={() => deleteCard(card.id)}>
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            ))}
            {adding && (
              <Elements stripe={stripePromise}>
                <AddCardForm userId={user.id} onAdded={() => { setAdding(false); fetchCards(); }} onCancel={() => setAdding(false)} />
              </Elements>
            )}
          </div>
        )}

        {!adding && (
          <button className="btn btn-outline" style={{ fontSize: '13px' }} onClick={() => setAdding(true)}>
            <CreditCard size={14} style={{ marginRight: '6px' }} /> Add Card
          </button>
        )}
      </div>
    </div>
  );
};

export default PaymentCardSettings;
